import { faCalendarDays } from "@fortawesome/free-regular-svg-icons/faCalendarDays";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { footerSliceSelectors } from "../redux/footer-daily-stats/footer-daily-stats.selector";
import { footerActions } from "../redux/footer-daily-stats/footer-daily-stats.slice";
import { useAppDispatch, useAppSelector } from "../redux/store";
import { userSliceSelectors } from "../redux/user/user.selector";
import { Spacing } from "../theme/Spacing";
import { standardDate } from "../utils/Date";
import { AtiraIcon } from "./AtiraIcon";
import { Flex } from "./Flex";
import { FontSize, Text } from "./Text";
import { FloatingTodayTasks } from "./floating-today-tasks/FloatingTodayTasks";
import { EntityIcons } from "../utils/GlobalIcons";

const Container = styled(Flex)<{ sideMenuCollapsed: boolean }>`
  height: 2rem;
  background-color: ${({ theme }) => theme.main};
  position: fixed;
  bottom: 0;
  z-index: 100;
  transition: all 0.2s;
  inset-inline-end: 0;
  inset-inline-start: ${({ sideMenuCollapsed }) =>
    sideMenuCollapsed ? 60 : 190}px;
  padding-inline-start: ${Spacing.m};
  width: ${({ sideMenuCollapsed }) =>
    `calc(100% - ${sideMenuCollapsed ? 60 : 190}px)`};
  padding-block: 2px;
`;

const InfoContainer = styled(Flex)`
  align-items: center;
  gap: ${Spacing.s};
  flex-shrink: 0;

  p {
    font-size: ${FontSize.s};
  }
`;

const DateText = styled(Text)`
  padding-inline-end: ${Spacing.m};
  border-inline-end: ${({ theme }) => `2px solid ${theme.sub}`};
`;

const StatsWrapper = styled(Flex)`
  gap: ${Spacing.l};
  overflow-x: auto;
  padding-inline: ${Spacing.m};
  flex: 1;

  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;

  -webkit-mask-image: linear-gradient(
    to right,
    transparent,
    black 10%,
    black 98%,
    transparent
  );
  mask-image: linear-gradient(
    to right,
    transparent,
    black 0.5rem,
    black 98%,
    transparent
  );

  html[dir="rtl"] & {
    -webkit-mask-image: linear-gradient(
      to left,
      transparent,
      black 2%,
      black 98%,
      transparent
    );
    mask-image: linear-gradient(
      to left,
      transparent,
      black 0.5rem,
      black 98%,
      transparent
    );
  }

  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-size: 100% 100%;
  mask-size: 100% 100%;

  > * {
    flex-shrink: 0;
  }

  p {
    white-space: nowrap;
  }
`;

type DailyStatsFooterProps = {
  sideMenuCollapsed: boolean;
};

export const DailyStatsFooter: React.FC<DailyStatsFooterProps> = ({
  sideMenuCollapsed = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const stats = useAppSelector(footerSliceSelectors.selectFooterDailyStats);
  const user = useAppSelector(userSliceSelectors.selectLoggedInUser)!;

  const date = standardDate(new Date(), user);
  const todayEntries = stats?.entries?.today || 0;
  const todayLeadTasks = stats?.tasks?.lead?.today || 0;
  const todayContactTasks = stats?.tasks?.contact?.today || 0;
  const todayKanbanTasks = stats?.tasks?.kanban?.today || 0;

  useEffect(() => {
    dispatch(footerActions.getFooterDailyStats({ userId: user?._id }));
  }, [dispatch, user]);

  return (
    <Container sideMenuCollapsed={sideMenuCollapsed}>
      <InfoContainer>
        <AtiraIcon icon={faCalendarDays} color="sub" size="lg" />
        <DateText color="sub">{date}</DateText>
      </InfoContainer>

      <StatsWrapper>
        <InfoContainer>
          <AtiraIcon icon={EntityIcons.Tasks} color="sub" size="lg" />
          <Text color="sub">
            {t("common.todayLeadTasks")}
            {`: ${todayLeadTasks}`}
          </Text>
        </InfoContainer>

        <InfoContainer>
          <AtiraIcon icon={EntityIcons.Tasks} color="sub" size="lg" />
          <Text color="sub">
            {t("common.todayContactTasks")}
            {`: ${todayContactTasks}`}
          </Text>
        </InfoContainer>

        <InfoContainer>
          <AtiraIcon icon={EntityIcons.Tasks} color="sub" size="lg" />
          <Text color="sub">
            {t("common.todayKanbanTasks")}
            {`: ${todayKanbanTasks}`}
          </Text>
        </InfoContainer>

        <InfoContainer>
          <AtiraIcon icon={EntityIcons.Entries} color="sub" size="lg" />
          <Text color="sub">
            {t("common.todayEntries")}
            {`: ${todayEntries}`}
          </Text>
        </InfoContainer>
      </StatsWrapper>

      <FloatingTodayTasks />
    </Container>
  );
};
